<template>
  <BaseModal :title="$t('shafts.maintenance.new_job')" actions>
    <template #content>
      <AlertBar v-if="errors" :modifiers="['flat', 'light', 'padding']">
        {{ $t('shafts.jobs.error_message') }}
      </AlertBar>
      <div class="form__group">
        <FormRow
          :label="$t('shafts.issues.element_type')"
          labelFor="element_type"
          :modifiers="[
            'margin',
            'narrow',
            'lean',
            'lateral',
            errors && errors.element_type && 'error-bg',
          ]"
        >
          <div class="form-row__input">
            <Loader v-if="loading" />
            <Select
              v-else
              v-model="job.element_type"
              :placeholder="$t('shafts.jobs.unspecified')"
              :groups="elementTypeOptions"
            />
          </div>
        </FormRow>
      </div>
      <div class="form__group">
        <FormRow
          :label="$t('shafts.jobs.date')"
          :modifiers="[
            'margin',
            'narrow',
            'lean',
            'lateral',
            errors && errors.date && 'error-bg'
          ]"
        >
          <div class="form-row__input">
            <DatePicker
              v-model="job.date"
              :placeholder="$t('shafts.jobs.no_date')"
              :minDate="new Date()"
            />
          </div>
        </FormRow>
      </div>
    </template>
    <template #actions>
      <FormSubmitButton
        @click="save"
        :isSaving="isSaving"
      />
    </template>
  </BaseModal>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

import BaseModal from '@/components/generic/base_modal.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import FormRow from '@/components/form/row.vue';
import Select from '@/components/form/select.vue';
import DatePicker from '@/components/form/date_picker.vue';
import AlertBar from '@/components/form/alert_bar.vue';
import Loader from '@/components/generic/loader.vue';

import useAjax from '@/functions/use_ajax';
import { isoDate } from '@/functions/date_format_helpers';

const route = useRoute();
const ajax = useAjax();

const emit = defineEmits(['save']);

const job = reactive({
  element_type: null,
  date: new Date(),
});

const loading = ref(true);
const elementTypes = ref(null);
const isSaving = ref(false);
const errors = ref(null);

const elementTypeOptions = computed(() => {
  if (loading.value) return [];

  return [
    {
      label: I18n.t('shafts.issues.shaft_elements'),
      items: elementTypes.value.shaft_elements.map(element => ({
        label: I18n.t(`shafts.element_types.shaft.${element}`), value: `shaft/${element}`
      })),
    },
    {
      label: I18n.t('shafts.issues.station_elements'),
      items: elementTypes.value.station_template_member_types.map(element => ({
        label: I18n.t(`shafts.element_types.station.${element}`), value: `station/${element}`
      })),
    }
  ];
});

onMounted(async () => {
  loading.value = true;
  await loadElementTypes();
  loading.value = false;
});

function shaftUrl() {
  const { organisationId, shaftId } = route.params;
  return `/organisations/${organisationId}/shafts/${shaftId}`;
}

async function loadElementTypes() {
  const url = `${shaftUrl()}/elements`;

  let { ok, data } = await ajax(url);

  if (ok) {
    elementTypes.value = data;
  }
}

function formatJobForPost() {
  return {
    ...job,
    date: isoDate(job.date),
  };
}

async function save() {
  isSaving.value = true;

  const url = `${shaftUrl()}/jobs`;

  let { ok, data } = await ajax(url, { method: 'POST', body: { job: formatJobForPost() } });

  if (ok) {
    emit('save', data);
  } else {
    isSaving.value = false;
    errors.value = data.job;
  }
}
</script>
